import { Merchant } from "components/common/MerchantSearchAutoComplete";
import TransactionsType from "enums/TransactionsType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import { CustomFieldsRecord } from "hooks/customFields.types";
import { FormikAddress } from "utils/addressFormat";

export type BaseExpense = {
  expenseId?: string;
  expenseType: TransactionsType | null;
  description: string;
  payTo: {
    id: string;
    firstName: string;
    lastName?: string | null;
    email: string;
  };
  date: Date;
  amount: number;
  allocationCode: {
    id: "";
    allocationCode: "";
  };
  allocationType: {
    key: "";
    name: "";
  };
  attachments: LocalImageAsset[];
  lostReceipt: boolean;
  customFields?: CustomFieldsRecord;
};

export type CashExpense = BaseExpense & {
  merchant: Merchant | null;
};

export type MileageExpense = BaseExpense & {
  tripFrom: FormikAddress;
  tripTo: FormikAddress;
  distance: number;
  isRoundTrip: boolean;
  rate: number;
  polyline?: string;
};

export type FormikCreateExpense = CashExpense | MileageExpense;
export const baseReimbursementExpenseInitialValues: BaseExpense = {
  expenseId: "",
  expenseType: null,
  description: "",
  payTo: {
    id: "",
    firstName: "",
    lastName: "",
    email: "",
  },
  date: new Date(),
  amount: 0,
  allocationCode: {
    id: "",
    allocationCode: "",
  },
  allocationType: {
    key: "",
    name: "",
  },
  attachments: [],
  customFields: {},
  lostReceipt: false,
};

export const getCashExpenseInitialValues = (): CashExpense => ({
  ...baseReimbursementExpenseInitialValues,
  expenseType: TransactionsType.CASH,
  merchant: null,
  lostReceipt: false,
});

export const getMileageExpenseInitialValues = (
  rate: number = 0,
): MileageExpense => ({
  ...baseReimbursementExpenseInitialValues,
  expenseType: TransactionsType.MILEAGE,
  tripFrom: {} as FormikAddress,
  tripTo: {} as FormikAddress,
  distance: 0,
  isRoundTrip: false,
  rate,
  polyline: "",
});

export const getExpenseInitialValuesByType = (
  type: TransactionsType | null,
  mileageRate?: number,
): FormikCreateExpense => {
  switch (type) {
    case TransactionsType.CASH:
      return getCashExpenseInitialValues();
    case TransactionsType.MILEAGE:
      return getMileageExpenseInitialValues(mileageRate || 0);
    default:
      return baseReimbursementExpenseInitialValues as FormikCreateExpense;
  }
};

export const formatExpenseForFormik = (
  fetchedExpense: any,
  transformCustomFieldsToRecord: (
    fields: any,
    settings: any,
  ) => CustomFieldsRecord,
  customFieldSettings: any[],
  mileageRate?: number,
): FormikCreateExpense => {
  const transformedAttachments: LocalImageAsset[] =
    fetchedExpense.receiptDetail?.assets?.map((asset: any) => ({
      assetUrl: asset.assetUrl,
      extension: asset.extension,
      id: asset.id,
      isExisting: true,
    })) || [];

  const baseValues: BaseExpense = {
    expenseId: fetchedExpense.id,
    expenseType: fetchedExpense.type,
    description: fetchedExpense.memo || "",
    payTo: {
      id: fetchedExpense.user.id,
      firstName: fetchedExpense.user.firstName,
      lastName: fetchedExpense.user.lastName,
      email: fetchedExpense.user.email,
    },
    date: new Date(fetchedExpense.transactionDate),
    amount: Math.abs(fetchedExpense.amount) || 0,
    attachments: transformedAttachments,
    allocationCode: {
      id: fetchedExpense.allocationCode?.id || "",
      allocationCode: fetchedExpense.allocationCode?.allocationCode || "",
    },
    allocationType: {
      key: fetchedExpense.allocationType?.key || "",
      name: fetchedExpense.allocationType?.name || "",
    },
    customFields: transformCustomFieldsToRecord(
      fetchedExpense?.customFields,
      customFieldSettings,
    ),
    lostReceipt: fetchedExpense.lostReceipt || false,
  };

  // Add type-specific fields
  if (fetchedExpense.type === TransactionsType.CASH) {
    return {
      ...baseValues,
      merchant: {
        cachedLogo: fetchedExpense.merchantData?.cachedLogo || "",
        id: fetchedExpense.merchantData?.merchantId || "",
        logo: fetchedExpense.merchantData?.logo || "",
        name: fetchedExpense.merchantData?.name || "",
        url: fetchedExpense.merchantData?.url || "",
      } as Merchant,
    } as CashExpense;
  }

  if (fetchedExpense.type === TransactionsType.MILEAGE) {
    return {
      ...baseValues,
      tripFrom: {
        addressName: fetchedExpense.mileage?.tripFrom?.addressName || "",
        address1: fetchedExpense.mileage?.tripFrom?.address1 || "",
        address2: fetchedExpense.mileage?.tripFrom?.address2 || "",
        city: fetchedExpense.mileage?.tripFrom?.city || "",
        state: fetchedExpense.mileage?.tripFrom?.state || "",
        zipcode: fetchedExpense.mileage?.tripFrom?.zipcode || "",
        country: fetchedExpense.mileage?.tripFrom?.country || "",
        geocode: {
          x: fetchedExpense.mileage?.tripFrom?.geocode?.x || 0,
          y: fetchedExpense.mileage?.tripFrom?.geocode?.y || 0,
        },
      } as FormikAddress,
      tripTo: {
        addressName: fetchedExpense.mileage?.tripTo?.addressName || "",
        address1: fetchedExpense.mileage?.tripTo?.address1 || "",
        address2: fetchedExpense.mileage?.tripTo?.address2 || "",
        city: fetchedExpense.mileage?.tripTo?.city || "",
        state: fetchedExpense.mileage?.tripTo?.state || "",
        zipcode: fetchedExpense.mileage?.tripTo?.zipcode || "",
        country: fetchedExpense.mileage?.tripTo?.country || "",
        geocode: {
          x: fetchedExpense.mileage?.tripTo?.geocode?.x || 0,
          y: fetchedExpense.mileage?.tripTo?.geocode?.y || 0,
        },
      } as FormikAddress,
      distance: fetchedExpense?.mileage?.miles || 0,
      isRoundTrip: fetchedExpense?.mileage?.isRoundTrip || false,
      rate: mileageRate || 0,
      polyline: fetchedExpense?.mileage?.polyline || "",
    } as MileageExpense;
  }

  return baseValues as FormikCreateExpense;
};
